import { FormattedDate } from "@dgs/core";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import {
	BaseTravelSegment,
	BaseTravelSegmentCar,
	BaseTravelSegmentFlight,
	BaseTravelSegmentShuttle,
	BaseTravelSegmentTrain,
	TravelDirection,
} from "../../../../shared/types/travelSegment";
import { TravelSegmentDateRange } from "../TravelSegmentDateRange";
import { travelSegmentIdentifier } from "../travelSegmentIdentifier";
import { CardBody, CardFooter, CardHeader, TravelCard } from "./Card";
import { LabeledValue } from "./LabeledValue";
import { Leg, LegDestination } from "./Leg";

interface Props<T extends BaseTravelSegment> {
	direction: TravelDirection;
	travelSegment: T;
	heading: string;
	onRemove?: () => void;
}

export const TravelSegmentCard: FC<Props<BaseTravelSegment>> = ({ travelSegment, ...props }) => {
	switch (travelSegment.type) {
		case "car":
			return <TravelSegmentCarCard travelSegment={travelSegment} {...props} />;
		case "train":
			return <TravelSegmentTrainCard travelSegment={travelSegment} {...props} />;
		case "flight":
			return <TravelSegmentFlightCard travelSegment={travelSegment} {...props} />;
		case "shuttle":
			return <TravelSegmentShuttleCard travelSegment={travelSegment} {...props} />;
	}
};

const TravelSegmentFlightCard: FC<Props<BaseTravelSegmentFlight>> = ({ travelSegment, heading, onRemove }) => {
	const { t } = useTranslation();

	return (
		<TravelCard>
			<CardHeader onRemove={onRemove}>{heading}</CardHeader>
			<CardBody>
				<Leg
					iconSrc="/icons/travel.svg"
					departure={
						<LegDestination
							alignment="left"
							name={`(${travelSegment.departureLocation?.iataCode}) ${travelSegment.departureLocation?.name}`}
							time={travelSegment.departureTime}
						/>
					}
					arrival={
						<LegDestination
							alignment="right"
							name={`(${travelSegment.arrivalLocation?.iataCode}) ${travelSegment.arrivalLocation?.name}`}
							time={travelSegment.arrivalTime}
						/>
					}
				/>
			</CardBody>
			<CardFooter>
				<LabeledValue label={t("Date")}>
					<TravelSegmentDateRange arrivalDate={travelSegment.arrivalDate} departureDate={travelSegment.departureDate} />
				</LabeledValue>
				<LabeledValue label={t("Flight number")}>{travelSegmentIdentifier(travelSegment)}</LabeledValue>
				<LabeledValue label={t("Service class")}>{travelSegment.serviceClass || "-"}</LabeledValue>
			</CardFooter>
		</TravelCard>
	);
};

const TravelSegmentTrainCard: FC<Props<BaseTravelSegmentTrain>> = ({ travelSegment, heading, onRemove }) => {
	const { t } = useTranslation();

	return (
		<TravelCard>
			<CardHeader onRemove={onRemove}>{heading}</CardHeader>
			<CardBody>
				<Leg
					iconSrc="/icons/train.svg"
					departure={
						<LegDestination
							alignment="left"
							name={travelSegment.departureLocation}
							time={travelSegment.departureTime}
						/>
					}
					arrival={
						<LegDestination alignment="right" name={travelSegment.arrivalLocation} time={travelSegment.arrivalTime} />
					}
				/>
			</CardBody>
			<CardFooter>
				<LabeledValue label={t("Date")}>
					<TravelSegmentDateRange arrivalDate={travelSegment.arrivalDate} departureDate={travelSegment.departureDate} />
				</LabeledValue>
				<LabeledValue label={t("Train number")}>{travelSegmentIdentifier(travelSegment)}</LabeledValue>
				<LabeledValue label={t("Service class")}>{travelSegment.serviceClass || "-"}</LabeledValue>
			</CardFooter>
		</TravelCard>
	);
};

const TravelSegmentCarCard: FC<Props<BaseTravelSegmentCar>> = ({ direction, travelSegment, heading, onRemove }) => {
	const { t } = useTranslation();

	return (
		<TravelCard>
			<CardHeader onRemove={onRemove}>{heading}</CardHeader>
			<CardBody>
				<Leg
					iconSrc="/icons/car.svg"
					departure={
						<LegDestination
							alignment="left"
							name={travelSegment.departureLocation}
							time={`${direction === "arrival" ? travelSegment.arrivalTime : travelSegment.departureTime}`}
						/>
					}
					arrival={<LegDestination alignment="right" name={travelSegment.arrivalLocation} time="" />}
				/>
			</CardBody>
			<CardFooter>
				<LabeledValue label={t("Date")}>
					<FormattedDate
						date={direction === "arrival" ? travelSegment.arrivalDate : travelSegment.departureDate}
						format={{ day: "2-digit", month: "2-digit" }}
					/>
				</LabeledValue>
				<LabeledValue label={t("License plate")}>{travelSegmentIdentifier(travelSegment)}</LabeledValue>
				<LabeledValue label={t("Parking spot")}>{travelSegment.parkingSpot || "-"}</LabeledValue>
				<LabeledValue label={t("Charging station")}>{travelSegment.chargingStation || "-"}</LabeledValue>
			</CardFooter>
		</TravelCard>
	);
};

const TravelSegmentShuttleCard: FC<Props<BaseTravelSegmentShuttle>> = ({ travelSegment, heading, onRemove }) => {
	const { t } = useTranslation();

	return (
		<TravelCard>
			<CardHeader onRemove={onRemove}>{heading}</CardHeader>
			<CardBody>
				<Leg
					iconSrc="/icons/shuttle.svg"
					departure={<LegDestination alignment="left" time={travelSegment.departureTime} />}
					arrival={<LegDestination alignment="right" time={travelSegment.arrivalTime} />}
				/>
			</CardBody>
			<CardFooter>
				<LabeledValue label={t("Date")}>
					<TravelSegmentDateRange arrivalDate={travelSegment.arrivalDate} departureDate={travelSegment.departureDate} />
				</LabeledValue>
				<LabeledValue label={t("Name")}>{travelSegmentIdentifier(travelSegment)}</LabeledValue>
			</CardFooter>
		</TravelCard>
	);
};
